import {
  SkeletonCircle,
  SkeletonText,
  StackProps,
  Text,
  VStack,
} from "@chakra-ui/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { ProfileProgress } from "../../../components/card/ProfileProgress";
import { PageContainer } from "../../../components/container/PageContainer";
import { myStoreNavsSupplier } from "../../../constants/PageNavs";
import { SupplierStoreProfileInterface } from "../../../constants/SupplierProfile";
import useCustomToast from "../../../lib/useCustomToast";
import { useTriggerRenderStore } from "../../../store/useTriggerRenderStore";
import { axiosSupplier } from "../../../utils/AxiosInstance";
import { ProfileComplete } from "../../../utils/etc";
import { TabSupplierRoot } from "./TabSupplierRoot";
import { useUserHeader } from "../../../store/useUserHeader";
import { SpinnerLoading } from "../../../components/spinner/SpinnerLoading";

const ContainerEditableAvatar = lazy(
  () => import("../../../components/input/ContainerEditableAvatar")
);

interface Props extends StackProps {
  pageType: number;
}

export const MyStoreSupplierProfile = ({ pageType, ...rest }: Props) => {
  const [data, setData] = useState<SupplierStoreProfileInterface | undefined>(
    undefined
  );
  const [loaded, setLoaded] = useState<boolean>(false);

  const [statusProfile, setStatusProfile] = useState<
    ProfileComplete[] | undefined
  >();
  const { statusData, setStatusData } = useTriggerRenderStore();
  const [countProgress, setCountProgress] = useState<number>(0);
  const { fetchUserHeader, fetchUserNonActive } = useUserHeader();
  const toast = useCustomToast();

  useEffect(() => {
    axiosSupplier
      .get(`/v1/supplier/supplier-store-profile`)
      .then((res) => {
        setData(res.data.data);
      })
      .catch((err) => {
        toast({
          title: err?.response?.data?.message || "",
        });
      })
      .finally(() => {
        setLoaded(true);
      });
  }, [statusData]);

  useEffect(() => {
    let mustFilledSupplierData: ProfileComplete[] = [
      {
        id: 1,
        name: "Pemilik Toko",
        isComplete: !!data?.profile.name,
      },
      {
        id: 2,
        name: "Toko Supplier",
        isComplete: !!data?.profile.company_name,
      },
      {
        id: 3,
        name: "Logo",
        isComplete: !!data?.profile.logo,
      },
      {
        id: 4,
        name: "Alamat",
        isComplete:
          !!data?.shipping.provinsi &&
          !!data?.shipping.kota &&
          !!data?.shipping.kelurahan &&
          !!data.shipping.kecamatan,
      },
      {
        id: 5,
        name: "Bank",
        isComplete:
          !!data?.bankInformation.bank_name &&
          !!data?.bankInformation.acc_name &&
          !!data?.bankInformation.acc_number,
      },
    ];

    setStatusProfile(mustFilledSupplierData);
  }, [
    data?.bankInformation.acc_name,
    data?.bankInformation.acc_number,
    data?.bankInformation.bank_name,
    data?.profile.company_name,
    data?.profile.logo,
    data?.profile.name,
    data?.shipping.kecamatan,
    data?.shipping.kelurahan,
    data?.shipping.kota,
    data?.shipping.provinsi,
  ]);

  useEffect(() => {
    let total: number = 0;

    const arrMustFilled = [
      data?.profile.name,
      data?.profile.company_name,
      data?.profile.logo,
      data?.shipping.address,
      data?.shipping.provinsi,
      data?.shipping.kota,
      data?.shipping.kecamatan,
      data?.bankInformation.bank_name,
      data?.bankInformation.acc_name,
      data?.bankInformation.acc_number,
    ];

    arrMustFilled &&
      arrMustFilled.map((item) => {
        !!item ? (total += 10) : (total += 0);
      });

    setCountProgress(total);
  }, [data]);

  return (
    <PageContainer navs={myStoreNavsSupplier}>
      <VStack
        className="supplier-profile-page"
        w={"100%"}
        gap={4}
        align={"stretch"}
        {...rest}
      >
        {loaded && countProgress < 100 && (
          <ProfileProgress
            title="Kelengkapan Profil Supplier"
            countProgress={countProgress}
            statusProfile={statusProfile}
          />
        )}

        {!loaded ? (
          <SkeletonCircle alignSelf={"center"} size={"40"} />
        ) : (
          <Suspense fallback={<SpinnerLoading />}>
            <ContainerEditableAvatar
              name={data?.profile.name || data?.profile.company_name || "BL"}
              alignSelf={"center"}
              pageType={pageType}
              endpoint={`/v1/supplier/update-photo`}
              src={
                data?.profile.logo
                  ? `${process.env.REACT_APP_MEDIA_URL}${data?.profile.logo}`
                  : ""
              }
              onRefreshAvatar={() => {
                setStatusData();
                fetchUserHeader(
                  data?.profile.id,
                  `${data?.profile.role}`,
                  true
                );
                fetchUserNonActive(
                  data?.profile.id,
                  `${data?.profile.role}`,
                  true
                );
              }}
            />
          </Suspense>
        )}

        <VStack className="wrap-avatar-name" w={"100%"} gap={loaded ? 0 : 1}>
          <SkeletonText isLoaded={loaded} noOfLines={1}>
            {data?.profile.name && (
              <Text textStyle={"h-reg-title"}>{data.profile.company_name}</Text>
            )}
          </SkeletonText>

          <SkeletonText isLoaded={loaded} noOfLines={1}>
            {data?.profile.name && (
              <Text textStyle={"b-reg"} variant={"secondary"}>
                {data.profile.name}
              </Text>
            )}
          </SkeletonText>
        </VStack>

        <TabSupplierRoot
          data={data}
          children={undefined}
          loaded={loaded}
          onRefreshProfile={() => {
            setStatusData();
            fetchUserHeader(data?.profile.id, `${data?.profile.role}`, true);
            fetchUserNonActive(data?.profile.id, `${data?.profile.role}`, true);
          }}
        />
      </VStack>
    </PageContainer>
  );
};
